import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { FreshStrawRow } from '../FreshStrawActionPanel/payload';
import {
  entryTotal,
  FertilizationLike,
  formatPartnershipSummary,
  getOrInitRow,
} from './shared';

export interface MobileCardListProps {
  rows: FertilizationLike[];
  selectedRows: FreshStrawRow[];
  loading: boolean;
  error: unknown;
  onToggleRow: (fertilizationId: string) => void;
  onOpenStageGrade: (fertilizationId: string) => void;
  onOpenFertEdit: (fertilizationId: string) => void;
}

/**
 * Mobile-first card list for fertilization rows. Each card is a large
 * touch target — tapping the body toggles selection (48px minimum), and a
 * secondary "Stage / Grade" button opens the per-row dialog. Shows the core
 * counts in a compact stat row below the mating name.
 */
export function MobileCardList({
  rows,
  selectedRows,
  loading,
  error,
  onToggleRow,
  onOpenStageGrade,
  onOpenFertEdit,
}: MobileCardListProps) {
  if (error) {
    return (
      <p className="text-destructive p-4 text-sm">
        Failed to load fertilizations.
      </p>
    );
  }
  if (loading) {
    return <p className="text-muted-foreground p-4 text-sm">Loading…</p>;
  }
  if (rows.length === 0) {
    return (
      <p className="text-muted-foreground p-4 text-sm">
        No fertilizations match the current filters.
      </p>
    );
  }

  const selectedIds = new Set(selectedRows.map((r) => r.fertilizationId));

  return (
    <ul className="flex flex-col gap-2 p-2" role="list">
      {rows.map((f) => {
        const id = f.fertilizationId ?? '';
        const selected = selectedIds.has(id);
        const row = getOrInitRow(selectedRows, id);
        const sgTotal = entryTotal(row.entries);

        return (
          <li
            key={id}
            className={cn(
              'ring-border bg-background rounded-lg p-3 ring-1 transition-colors',
              selected && 'bg-primary/10 ring-primary',
            )}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggleRow(id)}
                aria-label={`Select ${f.mating ?? id}`}
                className="mt-1 h-5 w-5"
              />
              <button
                type="button"
                className="flex-1 text-left"
                onClick={() => onToggleRow(id)}
              >
                <p className="text-foreground text-sm font-medium">
                  {f.mating ?? id}
                </p>
                {f.donorName && (
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {f.donorName}
                  </p>
                )}
                {(() => {
                  const split = formatPartnershipSummary(f.partnership);
                  return split ? (
                    <p className="text-muted-foreground mt-0.5 text-[11px] italic">
                      {split}
                    </p>
                  ) : null;
                })()}
                <dl className="text-muted-foreground mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs tabular-nums">
                  <div className="flex justify-between">
                    <dt>Lab Shp</dt>
                    <dd>{f.origShipped ?? 0}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Ship Client</dt>
                    <dd>{f.clientShipped ?? 0}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Client Trf</dt>
                    <dd>{f.clientTransfer ?? 0}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Client Frz</dt>
                    <dd>{f.clientFrozen ?? 0}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Client Dc</dt>
                    <dd>{f.clientDiscarded ?? 0}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>S/G Entered</dt>
                    <dd
                      className={cn(
                        sgTotal > 0 && 'text-foreground font-semibold',
                      )}
                    >
                      {sgTotal}
                    </dd>
                  </div>
                </dl>
              </button>
            </div>
            <div className="mt-2 flex justify-end gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onOpenFertEdit(id)}
              >
                Edit
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onOpenStageGrade(id)}
              >
                Stage / Grade
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
